import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// Hotel Config → Lock System
// Read-only overview of the lock vendor this property uses, plus quick
// links to manage agents. The actual vendor + transport + encoder map is
// configured per-agent on the Lock Agents screen (one agent per Reception
// PC). This screen is the "what vendor are we on" landing card.
export default function PmsLockVendorConfig({ token, selectedProperty, onNavigateToAgents }) {
  const [vendors, setVendors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [vRes, aRes] = await Promise.all([
        fetch('/api/v1/lock/vendors', { headers: headers() }),
        fetch('/api/v1/lock/agents',  { headers: headers() })
      ]);
      if (!vRes.ok) throw new Error('Failed to load vendor catalog');
      if (!aRes.ok) throw new Error('Failed to load agents');
      const v = await vRes.json();
      const a = await aRes.json();
      setVendors(v.vendors || []);
      setAgents(a.agents || []);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load, selectedProperty?.id]);

  const [editAgent, setEditAgent] = useState(null); // agent row being edited
  const [saving, setSaving] = useState(false);

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete agent "${a.display_name}"? This permanently removes it. If it has card history it can't be deleted — revoke it instead.`)) return;
    try {
      const res = await fetch(`/api/v1/lock/agents/${a.agent_id}`, { method: 'DELETE', headers: headers() });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Delete failed');
      await load();
    } catch (e) { alert(e.message); }
  };

  const handleSaveEdit = async (fields) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/v1/lock/agents/${editAgent.agent_id}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(fields)
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Update failed');
      setEditAgent(null);
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const activeVendorKey = agents[0]?.vendor;
  const activeVendor = vendors.find((v) => v.vendor_key === activeVendorKey);

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <h2 style={{ margin: 0, color: '#1e293b' }}>Lock System</h2>
      <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 4, marginBottom: 20 }}>
        Which on-premise lock vendor this property uses, and which Reception PCs are bridged to EASEPMS
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : (
        <>
          {/* Active vendor card */}
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: 18, marginBottom: 16
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Active vendor
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#0f172a', marginTop: 4 }}>
                  {activeVendor ? activeVendor.display_name : 'No agent registered yet'}
                </div>
                {activeVendor && (
                  <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 6 }}>
                    Vendor key: <code>{activeVendor.vendor_key}</code>
                    {' · '}
                    Door audit: {activeVendor.supports_door_events ? 'Supported' : 'Not supported'}
                    {' · '}
                    Auth slots: {activeVendor.auth_slot_count}
                  </div>
                )}
              </div>
              <a href="#lock-agents" onClick={(e) => { e.preventDefault(); onNavigateToAgents && onNavigateToAgents(); }}
                 style={{ ...btnPrimary, textDecoration: 'none' }}>
                {activeVendor ? 'Manage agents →' : 'Register first agent →'}
              </a>
            </div>
          </div>

          {/* Supported vendors */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '1rem' }}>Supported vendor adapters</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {vendors.map((v) => {
                const isActive = v.vendor_key === activeVendorKey;
                return (
                  <div key={v.vendor_key}
                       style={{
                         border: isActive ? '2px solid #2563eb' : '1px solid #e5e7eb',
                         background: isActive ? '#eff6ff' : '#fff',
                         borderRadius: 8, padding: 14
                       }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{v.display_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>{v.vendor_key}</div>
                    <ul style={{ fontSize: '0.8rem', color: '#475569', margin: '8px 0 0', paddingLeft: 18 }}>
                      <li>Door events: {v.supports_door_events ? 'Yes' : 'No'}</li>
                      <li>Auth slots: {v.auth_slot_count}</li>
                      <li>Reserved encoders: {(v.reserved_encoder_ids || []).join(', ') || 'none'}</li>
                    </ul>
                    {isActive && (
                      <div style={{
                        marginTop: 10, fontSize: '0.75rem', color: '#1e40af', fontWeight: 600
                      }}>● Active for this property</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 12 }}>
              Need a different lock vendor? Adding a new adapter requires the vendor's PMS protocol PDF + an engineering change. The list above reflects every adapter the agent EXE ships with today.
            </div>
          </div>

          {/* Agent summary table */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1rem' }}>Bridge agents at this property</h3>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{agents.length} agent{agents.length === 1 ? '' : 's'}</span>
            </div>
            {agents.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                No bridge agents registered yet. Open <b>Lock Agents</b> in the menu to register the first one.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={th}>Name</th>
                    <th style={th}>Status</th>
                    <th style={th}>Vendor</th>
                    <th style={th}>Transport</th>
                    <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => (
                    <tr key={a.agent_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}>{a.display_name}</td>
                      <td style={td}>{a.status}</td>
                      <td style={td}>{a.vendor}</td>
                      <td style={td}>{a.transport_kind} · {a.transport_target}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button onClick={() => setEditAgent(a)} style={actionBtn} title="Edit name / transport">Edit</button>
                        <button onClick={() => handleDelete(a)} style={{ ...actionBtn, color: '#991b1b', borderColor: '#fca5a5' }} title="Permanently delete this agent">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {editAgent && (
        <EditAgentModal
          agent={editAgent}
          saving={saving}
          onClose={() => setEditAgent(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}

function EditAgentModal({ agent, saving, onClose, onSave }) {
  const { isMobile } = useIsMobile();
  const [displayName, setDisplayName] = useState(agent.display_name || '');
  const [transportKind, setTransportKind] = useState(agent.transport_kind || '');
  const [transportTarget, setTransportTarget] = useState(agent.transport_target || '');

  const submit = (e) => {
    e.preventDefault();
    if (!displayName.trim()) { alert('Display name cannot be empty'); return; }
    onSave({
      display_name: displayName.trim(),
      transport_kind: transportKind.trim() || null,
      transport_target: transportTarget.trim() || null
    });
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={isMobile ? { ...modal, width: '100%', padding: 16, boxSizing: 'border-box' } : modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', color: '#1e293b' }}>Edit agent</h3>
        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 16 }}>
          Vendor <b>{agent.vendor}</b> · Agent ID {agent.agent_id}. Vendor can’t be changed here — re-register to switch vendors.
        </div>
        <form onSubmit={submit}>
          <label style={lbl}>Display name</label>
          <input style={inp} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Reception PC 1" autoFocus />
          <label style={lbl}>Transport</label>
          <input style={inp} value={transportKind} onChange={(e) => setTransportKind(e.target.value)} placeholder="e.g. local_rest, dll, tcp, serial" />
          <label style={lbl}>Transport target</label>
          <input style={inp} value={transportTarget} onChange={(e) => setTransportTarget(e.target.value)} placeholder="e.g. http://127.0.0.1:8833/betech/api/v1" />
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>
            This is the cloud record only — the agent’s live transport comes from its own appsettings.json.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} style={{ ...actionBtn, padding: '8px 16px' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '8px 10px', color: '#0f172a' };
const btnPrimary = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const actionBtn = { background: '#fff', border: '1px solid #cbd5e1', color: '#334155', padding: '5px 12px', borderRadius: 6, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', marginLeft: 6 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal = { background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '92vw', boxShadow: '0 20px 45px rgba(0,0,0,0.25)' };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', margin: '10px 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' };
